import React, { useState } from "react";
import { Container, Table, Button, Card, Spinner, Alert } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import PageTitle from "@/common/PageTitle/PageTitle";
import {
  useListBrandLocationsQuery,
  type BrandLocationRow,
} from "@/features/brands/brandsApi";
import styles from "./brand-locations.module.scss";

function formatLocationAddress(row: BrandLocationRow): string {
  if (row.address) return row.address;
  const parts = [
    row.address_street,
    row.address_city,
    row.address_state,
    row.address_zipCode,
  ].filter(Boolean);
  return parts.join(", ");
}

function statusLabel(status: string | null | undefined): "Active" | "Inactive" {
  const s = String(status || "active").toLowerCase();
  return s === "inactive" ? "Inactive" : "Active";
}

const BrandLocations: React.FC = () => {
  const { brandId } = useParams<{ brandId: string }>();
  const navigate = useNavigate();
  const [expandedLocations, setExpandedLocations] = useState<Set<string>>(
    new Set(),
  );

  const { data, isLoading, isError, error, refetch } = useListBrandLocationsQuery(
    brandId || "",
    { skip: !brandId },
  );

  const brandName = data?.brandName || "Brand";
  const locations = data?.locations || [];

  const handleBackToBrands = () => {
    navigate("/brands");
  };



  const toggleLocationExpansion = (locationId: string) => {
    const newExpanded = new Set(expandedLocations);
    if (newExpanded.has(locationId)) {
      newExpanded.delete(locationId);
    } else {
      newExpanded.add(locationId);
    }
    setExpandedLocations(newExpanded);
  };

  const isLocationExpanded = (locationId: string) => {
    return expandedLocations.has(locationId);
  };

  return (
    <Container fluid>
      <Card style={{ borderRadius: 12 }}>
        <Card.Body>
          <Card.Title
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <PageTitle title={`${brandName} - Locations`} />
            <Button
              variant="outline-secondary"
              onClick={handleBackToBrands}
              className={styles.backButton}
            >
              ← Back to Brands
            </Button>
          </Card.Title>

          <div className={styles.pageHeader}>
            <div className={styles.headerInfo}>
              <h3 className={styles.pageSubtitle}>
                Locations ({locations.length})
              </h3>
              <p className={styles.pageDescription}>
                Canonical Locations belonging to {brandName}. Master Accounts
                operate Locations via assignment — they do not own them.
              </p>
            </div>
            <Button
              className={styles.createLocationButton}
              disabled
              title="Create Location is deferred (future self-service for authorized operators)"
            >
              Create Location
            </Button>
          </div>

          {isLoading && (
            <div className="text-center py-4" data-testid="brand-locations-loading">
              <Spinner animation="border" />
            </div>
          )}

          {isError && (
            <Alert variant="danger" data-testid="brand-locations-error">
              {(error as { data?: { message?: string } })?.data?.message ||
                "Failed to load brand locations"}{" "}
              <Button size="sm" onClick={() => refetch()}>
                Retry
              </Button>
            </Alert>
          )}

          {!isLoading && !isError && locations.length === 0 && (
            <Alert variant="info" data-testid="brand-locations-empty">
              No canonical Locations for this Brand yet.
            </Alert>
          )}

          {!isLoading && !isError && locations.length > 0 && (
            <Table hover responsive className={styles.locationsTable}>
              <thead>
                <tr>
                  <th></th>
                  <th>Location Name</th>
                  <th>Address</th>
                  <th>Status</th>
                  <th>Zones</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {locations.map((location) => {
                  const id = location.locationId;
                  const status = statusLabel(location.status);
                  return (
                    <React.Fragment key={id}>
                      <tr className={styles.locationRow}>
                        <td>
                          <span
                            className={`${styles.expandButton} ${
                              isLocationExpanded(id) ? styles.expanded : ""
                            }`}
                            onClick={() => toggleLocationExpansion(id)}
                          >
                            {isLocationExpanded(id) ? "▼" : "▶"}
                          </span>
                        </td>
                        <td>{location.locationName}</td>
                        <td>{formatLocationAddress(location)}</td>
                        <td>
                          <span
                            className={`${styles.statusBadge} ${
                              status === "Active"
                                ? styles.statusActive
                                : styles.statusInactive
                            }`}
                          >
                            {status}
                          </span>
                        </td>
                        <td>—</td>
                        <td>
                          <button
                            type="button"
                            className={styles.actionButton}
                            disabled
                            title="Location actions (edit/transfer/delete) are deferred until canonical write contracts exist"
                            aria-label="Location actions unavailable"
                          >
                            ⋮
                          </button>
                        </td>
                      </tr>
                      {isLocationExpanded(id) && (
                        <tr className={styles.zonesRow}>
                          <td colSpan={6} className={styles.zonesCell}>
                            <div className={styles.zonesContainer}>
                              <h5 className={styles.zonesTitle}>Zones</h5>
                              <p className="text-muted mb-0">
                                Zone detail is not available yet. Canonical Zone
                                services are not connected in this phase.
                                {location.currentOperatorBusinessName
                                  ? ` Current commercial operator: ${location.currentOperatorBusinessName}.`
                                  : " No current commercial operator assignment."}
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default BrandLocations;
